/* React */
import { useState } from 'react'

/* Redux */
import Redux from './Redux'

/* Material */
import Typo from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import useStyles from './styles'

/* Components */
import Loading from 'src/components/Loading'
import GoogleRecaptchaInfos from 'src/components/GoogleRecaptchaInfos'
import { pageBuilderButtonSx } from 'src/theme/pageBuilderButtons'

/* Functions */
import loginRequest from './functions/loginRequest'
import getRecaptchaToken from 'src/functions/getRecaptchaToken'

interface RegistrationFormProps {
  checkout?: { email: string; onEmailChange: (email: string) => void; onSuccess: () => void }
}

const RegistrationForm = ({ checkout }: RegistrationFormProps) => {
  const classes = useStyles()
  const { reduxLogin } = Redux()

  const [localEmail, changeEmail] = useState<string>('')
  const email = checkout?.email ?? localEmail
  const [password, changePassword] = useState<string>('')

  const [loading, toggleLoading] = useState(false)
  const [tried, toggleTried] = useState(false)
  const [error, setError] = useState('')

  if (loading && !checkout) return <Loading />

  return <div>

    {!checkout && <Typo component='p' variant='body1' color='inherit' gutterBottom>
      Erstellen Sie ein neues Kundenkonto bei uns
    </Typo>}
    
    <form 
      id='form-register' 
      className={classes.registerForm}
      aria-busy={loading}
      onSubmit={async e=>{
        e.preventDefault()
        if (loading) return
        toggleLoading(true)
        toggleTried(true)
        setError('')
        try {
          const recaptchaToken = await getRecaptchaToken('registration')
          if (!recaptchaToken) throw new Error('recaptcha unavailable')
          const token = await loginRequest(email, password, true, recaptchaToken)
          if (token) {
            reduxLogin({ token, email })
            checkout?.onSuccess()
          } else setError(checkout
            ? 'Das Konto konnte nicht angelegt werden. Prüfe deine Angaben. Hast du bereits ein Konto? Wähle „Anmelden“.'
            : 'Das Konto konnte nicht angelegt werden. Bitte prüfen Sie Ihre Angaben oder melden Sie sich mit Ihrem bestehenden Konto an.')
        } catch {
          setError('Das Konto konnte gerade nicht angelegt werden. Bitte versuche es erneut.')
        } finally { toggleLoading(false) }
      }}
    >

      <TextField 
        placeholder='Email'
        label="E-Mail-Adresse"
        name='email'
        className='email'
        autoComplete='email'
        type='email'
        value={email} required disabled={loading} inputProps={{ maxLength: 100 }}
        onChange={({target:{value}})=>checkout ? checkout.onEmailChange(value) : changeEmail(value)}

        helperText={!checkout && tried && "Bitte geben Sie eine gültige Email-Adresse ein"}
      />
      
      <TextField 
        placeholder='Neues Passwort'
        label="Neues Passwort"
        name='new-password'
        className='new-password'
        autoComplete='new-password'
        type='password'
        value={password} required disabled={loading} inputProps={{ minLength: 8, maxLength: 100 }}
        onChange={({target:{value}})=>changePassword(value)}

        helperText={checkout ? 'Mindestens 8 Zeichen.' : tried && "Ihr Passwort muss mindestens 8 Zeichen lang sein"}
      />

      {error && <Alert severity='error'>{error}</Alert>}

      <div className={checkout ? undefined : classes.dsgvoRecaptcha}>
        <GoogleRecaptchaInfos compact={Boolean(checkout)} />
      </div>

      <Button 
        variant='contained' color='primary' size='large'
        type='submit'
        disabled={loading}
        className={classes.loginButton}
        sx={pageBuilderButtonSx}
      >
        {checkout ? loading ? 'Konto wird angelegt …' : 'Konto anlegen' : 'Registrieren'}
      </Button>

    </form>

  </div>
}

export default RegistrationForm
